import React, { useCallback, useEffect, useRef, useState } from 'react';

import { WidgetDropdownCategory } from 'widgets/WidgetCategory';
import { WidgetDropdownItem } from 'widgets/WidgetsService';
import classNames from 'classnames';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import { AddWidgetSelectedItem } from 'appToolbar/AddWidgetModal/AddWidgetModal';
import { WidgetType } from 'widgets/WidgetType';
import { useAddLayoutDragSourcePubSub } from '../AddLayoutDragSourcePubSub';

export interface AvailableWidgetsGroupProps {
    addMultiple: boolean;
    group: {
        category: WidgetDropdownCategory;
        options: WidgetDropdownItem[];
    };
    searchText: string;
    previewItem?: AddWidgetSelectedItem;
    selectedItems: AddWidgetSelectedItem[];
    onClickItem: (wt: AddWidgetSelectedItem) => void;
    onClickAddItem: (wt: WidgetType) => void;
}

export function AvailableWidgetsGroup(props: AvailableWidgetsGroupProps) {
    const {
        addMultiple,
        group,
        searchText,
        selectedItems,
        onClickAddItem,
        onClickItem,
        previewItem,
    } = props;
    const [expanded, setExpanded] = useState(true);

    const toggleExpanded = () => setExpanded(!expanded);

    const onClickAdd = useCallback(
        (wt: WidgetDropdownItem) => {
            onClickAddItem(wt.type);
        },
        [onClickAddItem]
    );

    if (group.options.length === 0) {
        return <></>;
    }

    const filteredOptions = group.options.filter(
        (o) =>
            !searchText ||
            o.title.toLowerCase().indexOf(searchText.toLowerCase()) > -1
    );

    if (filteredOptions.length === 0) {
        return <></>;
    }

    const onDoubleClick = (o: WidgetDropdownItem) => {
        // In single item mode, double click is the equivalent of clicking the add item button
        if (!addMultiple) {
            onClickAdd(o);
        }
    };

    return (
        <>
            {group.category.title && (
                <div
                    className={classNames('item title', expanded && 'active')}
                    onClick={toggleExpanded}
                >
                    <i className={`angle ${expanded ? 'up' : 'down'} icon`} />
                    <span>{group.category.title}</span>
                </div>
            )}
            {expanded &&
                filteredOptions.map((o) => (
                    <AddWidgetItem
                        key={o.type}
                        item={o}
                        addMultiple={addMultiple}
                        isSelected={
                            !!selectedItems.find(
                                (i) => i.widget.type === o.type
                            )
                        }
                        isPreviewItem={
                            !!(
                                previewItem?.widget.type === o.type &&
                                'preview-item'
                            )
                        }
                        onClickAdd={onClickAdd}
                        onClickItem={onClickItem}
                        onDoubleClick={onDoubleClick}
                    />
                ))}
        </>
    );
}

export function AddWidgetItem(props: {
    item: WidgetDropdownItem;
    addMultiple: boolean;
    isSelected: boolean;
    isPreviewItem: boolean;
    onClickAdd: (wt: WidgetDropdownItem) => void;
    onClickItem: (wt: AddWidgetSelectedItem) => void;
    onDoubleClick: (o: WidgetDropdownItem) => void;
}) {
    const {
        item,
        isSelected,
        isPreviewItem,
        addMultiple,
        onClickAdd,
        onClickItem,
        onDoubleClick,
    } = props;

    const dragSourceRef = useRef<HTMLDivElement>(null);
    const addLayoutDragSourcePubSub = useAddLayoutDragSourcePubSub();

    useEffect(() => {
        if (!dragSourceRef.current) {
            return;
        }
        addLayoutDragSourcePubSub.publishAdd({
            type: item.type,
            title: item.title,
            element: dragSourceRef.current,
            uid: 'AvailableWidgetsModal-' + item.type,
        });
    }, [addLayoutDragSourcePubSub, item]);

    return (
        <div
            className={classNames(
                'item widget',
                isSelected && 'selected',
                isPreviewItem && 'preview-item'
            )}
            data-widget-type={item.type}
            key={item.type}
            onClick={() => onClickItem({ widget: item })}
            onDoubleClick={() => onDoubleClick(item)}
            ref={dragSourceRef}
        >
            <span className="item-title">
                <div className="grab-icon">
                    <Icon name="grab" className="drag-button" />
                </div>{' '}
                {item.title}
            </span>
            {!addMultiple && (
                <div
                    className="item-add-button"
                    onClick={() => onClickAdd(item)}
                    title={`Add ${item.title}`}
                >
                    <Icon name="plus circle" className="add-item-button" />
                </div>
            )}
        </div>
    );
}
